import React from 'react';
import { Tooltip } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * EnhancedTooltip - A reusable tooltip component with consistent styling
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The element to attach the tooltip to
 * @param {string|React.ReactNode} props.title - The tooltip content
 * @param {string} props.placement - Tooltip placement ('top', 'bottom', 'left', 'right')
 * @param {boolean} props.arrow - Whether to show the arrow
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Additional inline styles
 * @param {boolean} props.disableHoverListener - Whether to disable hover listener
 * @param {boolean} props.disableFocusListener - Whether to disable focus listener
 * @param {boolean} props.disableTouchListener - Whether to disable touch listener
 * @param {number} props.enterDelay - Delay before showing tooltip (ms)
 * @param {number} props.leaveDelay - Delay before hiding tooltip (ms)
 * @param {Object} props.TooltipClasses - Custom classes for the tooltip
 * @param {Function} props.onOpen - Callback when tooltip opens
 * @param {Function} props.onClose - Callback when tooltip closes
 */
const EnhancedTooltip = ({
  children,
  title,
  placement = 'top',
  arrow = true,
  className = '',
  style = {},
  disableHoverListener = false,
  disableFocusListener = false,
  disableTouchListener = false,
  enterDelay = 200,
  leaveDelay = 0,
  TooltipClasses = {},
  onOpen,
  onClose,
  ...otherProps
}) => {
  return (
    <Tooltip
      title={title}
      placement={placement}
      arrow={arrow}
      className={className}
      style={style}
      disableHoverListener={disableHoverListener}
      disableFocusListener={disableFocusListener}
      disableTouchListener={disableTouchListener}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}
      classes={{
        tooltip: 'MuiTooltip-tooltip',
        arrow: 'MuiTooltip-arrow',
        ...TooltipClasses,
      }}
      onOpen={onOpen}
      onClose={onClose}
      {...otherProps}
    >
      {children}
    </Tooltip>
  );
};

EnhancedTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right', 'top-start', 'top-end', 'bottom-start', 'bottom-end', 'left-start', 'left-end', 'right-start', 'right-end']),
  arrow: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  disableHoverListener: PropTypes.bool,
  disableFocusListener: PropTypes.bool,
  disableTouchListener: PropTypes.bool,
  enterDelay: PropTypes.number,
  leaveDelay: PropTypes.number,
  TooltipClasses: PropTypes.object,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
};

export default EnhancedTooltip;
